import { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAppStore } from './stores/useAppStore';
import { publicApi } from './services/api';
import { HomePage, PaymentPage, AdminPage, TermsPage, AccountPage, AuthPage } from './pages';
import ProfileView from './pages/AccountPage/ProfileView';
import VerificationView from './pages/AccountPage/VerificationView';
import OrdersView from './pages/AccountPage/OrdersView';
import KycView from './pages/AccountPage/KycView';
import { useAuthStore } from './stores/useAuthStore';
import './i18n';
import './styles/legacy.css';
import './styles/asset-select.css';
import './styles/rx.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const { theme, setSettings, setPrices, setPaymentMethods, setFaqs, setSell } = useAppStore();
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (token) refreshUser();
  }, [token, refreshUser]);

  // Load site config on every page load
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await publicApi.getConfig();
        const data = response.data;
        setSettings(data.settings || {});
        setPrices(data.prices || {});
        setPaymentMethods(data.payment_methods || []);
        setFaqs(data.faqs || []);
        setSell(data.sell || null);
      } catch (error) {
        console.error('Failed to load site config:', error);
      }
    };

    loadConfig();
  }, [setSettings, setPrices, setPaymentMethods, setFaqs, setSell]);

  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
  }, [theme]);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/payment/:orderId" element={<PaymentPage />} />
      <Route path="/pay/:orderId" element={<PaymentPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/account" element={<AccountPage />}>
        <Route index element={<ProfileView />} />
        <Route path="verification" element={<VerificationView />} />
        <Route path="orders" element={<OrdersView />} />
        <Route path="kyc" element={<KycView />} />
      </Route>
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
